import { Color, Web3DViewer } from '@technology/web3d';
import {
  AlignmentNavigationTool,
  StartNavigationOptions,
  TrimbimModel,
} from '@technology/web3d-plugin-trimbim';
import { AlignmentSettings } from '@trimble/types';
import { getLogger } from '@trimble/logging';
import { CapabilityProviderBase } from '../base/provider-base';
import { getTrbModelId, getTrbModelName } from './trim-bim-model-name-helpers';
import {
  analyzeTrimbimAlignment,
  analyzeTrimbimModel,
} from './trimbim-alignment';
import {
  SectionMaterialConfig,
  TrajectoryMetadata,
} from './trimbim-alignment.types';
import { transformAlignmentTrimbim } from './trimbim-transform';

export class TrajectoryMetadataProvider extends CapabilityProviderBase<
  TrajectoryMetadata,
  'modelId'
> {
  public readonly idKey = 'modelId' as const;

  /**
   * Stores the original trimbim ArrayBuffers from the Alignment API,
   * keyed by model id. Used to re-transform locally when alignment
   * settings change without re-fetching from the API.
   */
  private readonly sourceBuffers = new Map<string, ArrayBuffer>();

  private readonly colorOverrides = new Map<string, Map<string, Color>>();

  private readonly desiredLoadedSections = new Map<string, string[]>();

  private readonly desiredSectionMaterials = new Map<
    string,
    SectionMaterialConfig[]
  >();

  private readonly appliedMaterialsSignature = new Map<string, string>();

  private readonly applyChains = new Map<string, Promise<void>>();

  constructor(public readonly viewer: Web3DViewer) {
    super();
  }

  /** Returns a copy of the original (untransformed) source buffer. */
  public getSourceBuffer(id: string): ArrayBuffer | undefined {
    const source = this.sourceBuffers.get(id);
    return source ? source.slice(0) : undefined;
  }

  /**
   * Returns `undefined` for items that are currently being analyzed, so
   * consumers never see stale or incomplete metadata.
   */
  public override getById(
    id: TrajectoryMetadata['modelId']
  ): TrajectoryMetadata | undefined {
    if (this.isItemLoading(id)) {
      return undefined;
    }
    return super.getById(id);
  }

  /**
   * Phase 1: Pure analysis -- operates only on the ArrayBuffer via FlatBuffers.
   * Can run in parallel with model loading since it does not need the viewer.
   * Also stores a copy of the source buffer for later local transformations.
   *
   * While analysis is in progress the item is marked as loading so that
   * `getById` returns `undefined`, preventing consumers from reading stale
   * metadata (e.g. with chainage-based distances instead of real distances).
   */
  public async analyzeAndStore(
    id: string,
    metadata: TrajectoryMetadata | undefined,
    trimBim: ArrayBuffer
  ): Promise<TrajectoryMetadata> {
    // Keep a copy of the original API buffer for future re-transforms
    this.sourceBuffers.set(id, trimBim.slice(0));

    const existing = super.getById(id);

    this.addToLoadingSet(id as TrajectoryMetadata['modelId']);

    try {
      const result =
        metadata === undefined
          ? await analyzeTrimbimModel(id, trimBim)
          : await analyzeTrimbimAlignment(metadata, trimBim);

      if (existing) {
        this.removeItem(existing);
      }
      this.addItem(result);
      return result;
    } finally {
      this.removeFromLoadingSet(id as TrajectoryMetadata['modelId']);
    }
  }

  /**
   * Stores pre-analyzed metadata and its source buffer directly, without
   * re-running the analysis. Used to replicate metadata across per-pane
   * providers so every pane can resolve alignment properties (e.g. isAlignment).
   *
   * The metadata is deep-cloned so each provider owns an independent copy,
   * preventing cross-pane corruption if any provider mutates the arrays.
   */
  public storeAnalyzedMetadata(
    metadata: TrajectoryMetadata,
    trimBim: ArrayBuffer
  ): void {
    const cloned = cloneTrajectoryMetadata(metadata);
    this.sourceBuffers.set(cloned.modelId, trimBim.slice(0));
    const existing = this.getById(cloned.modelId);
    if (existing) {
      this.removeItem(existing);
    }
    this.addItem(cloned);
  }

  /**
   * Transforms the stored source buffer for a model using the given
   * alignment settings.  Returns the transformed ArrayBuffer, or
   * undefined if no source is stored.
   *
   * Uses the trajectory metadata (section chainages) to compute the
   * horizontal offset perpendicular to the alignment direction.
   */
  public getTransformedBuffer(
    id: string,
    alignmentSettings: AlignmentSettings,
    lengthDecimals: number
  ): ArrayBuffer | undefined {
    const source = this.sourceBuffers.get(id);
    if (!source) return undefined;
    return transformAlignmentTrimbim(source, {
      alignmentSettings,
      lengthDecimals,
      trajectoryMetadata: this.getById(id),
    });
  }

  public async applyVisualsAndRestoreNavigation(
    id: string,
    navState?: StartNavigationOptions
  ): Promise<void> {
    const metadata = this.getById(id);
    const model = this.viewer.getModel(getTrbModelName(id));
    if (!model || !metadata) {
      return;
    }

    if (
      !this.desiredSectionMaterials.has(id) &&
      (metadata.sectionMaterials ?? []).length > 0
    ) {
      this.desiredSectionMaterials.set(id, metadata.sectionMaterials!);
    }

    // Only paint when the merged result would change — avoids wiping an
    // existing per-pane color override on redundant restore calls.
    await this.applyMaterialsAndSections(id);

    if (navState) {
      const trbModelName = getTrbModelName(id);
      const alignmentNavigationTool = this.viewer.tools.get(
        AlignmentNavigationTool
      ) as AlignmentNavigationTool | undefined;

      if (alignmentNavigationTool) {
        await alignmentNavigationTool.startNavigation(trbModelName, navState);
      }
    }
  }

  /**
   * Records (or clears when `color` is `undefined`) a color override and
   * re-applies section materials. `modelId` accepts the model id or `.trb`
   * name; empty `entityIds` overrides the whole trajectory.
   */
  public async setMaterialColors(
    modelId: string,
    entityIds: number[] | undefined,
    color: Color | undefined
  ): Promise<void> {
    const metadata = this.items().find(
      (m) => m.modelId === modelId || m.modelId === getTrbModelId(modelId)
    );
    if (!metadata?.sectionMaterials) {
      return;
    }

    const sectionNames = this.resolveSectionNames(metadata, entityIds);
    if (sectionNames.length === 0) {
      return;
    }

    this.updateColorOverrides(metadata.modelId, sectionNames, color);

    if (!this.desiredSectionMaterials.has(metadata.modelId)) {
      this.desiredSectionMaterials.set(
        metadata.modelId,
        metadata.sectionMaterials
      );
    }
    this.appliedMaterialsSignature.delete(metadata.modelId);
    await this.applyMaterialsAndSections(metadata.modelId);
  }

  private updateColorOverrides(
    modelId: string,
    sectionNames: readonly string[],
    color: Color | undefined
  ): void {
    let overrides = this.colorOverrides.get(modelId);
    if (!overrides) {
      overrides = new Map<string, Color>();
      this.colorOverrides.set(modelId, overrides);
    }

    for (const sectionName of sectionNames) {
      if (color) {
        // Clone so the stored override is independent from the viewer's instance.
        overrides.set(sectionName, color.clone());
      } else {
        overrides.delete(sectionName);
      }
    }

    if (overrides.size === 0) {
      this.colorOverrides.delete(modelId);
    }
  }

  /**
   * Returns a copy of `materials` with this pane's color overrides (or the
   * run-palette default) merged in. Never mutates the shared input array the
   * coordinator passes to every pane.
   */
  private applyColorOverridesToMaterials(
    pathId: string,
    materials: SectionMaterialConfig[]
  ): SectionMaterialConfig[] {
    const overrides = this.colorOverrides.get(pathId);
    return materials.map((sectionMaterial) => {
      const override = overrides?.get(sectionMaterial.sectionName);
      const color = override?.clone() ?? sectionMaterial.defaultColor?.clone();
      if (!color) {
        return sectionMaterial;
      }
      return {
        ...sectionMaterial,
        material: { ...sectionMaterial.material, color },
      };
    });
  }

  private resolveSectionNames(
    metadata: TrajectoryMetadata,
    entityIds: number[] | undefined
  ): string[] {
    const sectionMaterials = metadata.sectionMaterials ?? [];
    if (!entityIds || entityIds.length === 0) {
      return sectionMaterials.map((sm) => sm.sectionName);
    }

    const names = new Set<string>();
    for (const entityId of entityIds) {
      const sectionName = this.resolveSectionNameForEntity(metadata, entityId);
      if (sectionName) {
        names.add(sectionName);
      }
    }
    return [...names];
  }

  private resolveSectionNameForEntity(
    metadata: TrajectoryMetadata,
    entityId: number
  ): string | undefined {
    return metadata.sectionChainages?.find((sc) => sc.entityId === entityId)
      ?.sectionName;
  }

  /**
   * Saves navigation state if the given model is currently being navigated.
   * Call before unloading/reloading a model to preserve the user's position.
   *
   * Returns `undefined` when the tool is not navigating this model, OR when
   * the tool's internal model reference has become stale (which can happen
   * when `temporarilyDisableExitNavigationOnModelRemove` blocked the exit
   * but the underlying TrimbimModel was disposed — the tool then retains
   * `_alignmentModelId` and `_model`, but `model.findEntitiesByGeometryType`
   * throws inside `getNavigationState`).  Treating the failure as
   * "no nav state" lets multi-pane reloads continue on the other panes.
   */
  public async saveNavigationState(
    id: string
  ): Promise<StartNavigationOptions | undefined> {
    const trbModelName = getTrbModelName(id);
    const alignmentNavigationTool = this.viewer.tools.get(
      AlignmentNavigationTool
    ) as AlignmentNavigationTool | undefined;

    if (alignmentNavigationTool?.alignmentModelId !== trbModelName) {
      return undefined;
    }
    try {
      return await alignmentNavigationTool.getNavigationState();
    } catch (error) {
      getLogger().debug(
        `[TrajectoryMetadataProvider] saveNavigationState failed for ${id}`,
        error
      );
      return undefined;
    }
  }

  /**
   * Prepares this pane for a trajectory model reload. Always blocks nav exit
   * on model remove when this pane is navigating. Pass
   * `invalidateMaterials: true` only for the pane that will unload/reload the
   * model — clearing the signature on sibling panes forces a redundant
   * setSectionMaterials on the next coordinator sync.
   */
  public prepareForReload(
    id: string,
    options?: { invalidateMaterials?: boolean }
  ): void {
    const trbModelName = getTrbModelName(id);
    const alignmentNavigationTool = this.viewer.tools.get(
      AlignmentNavigationTool
    ) as AlignmentNavigationTool | undefined;

    if (alignmentNavigationTool?.alignmentModelId === trbModelName) {
      alignmentNavigationTool.temporarilyDisableExitNavigationOnModelRemove();
    }

    if (options?.invalidateMaterials) {
      this.appliedMaterialsSignature.delete(id);
    }
  }

  /**
   * Updates the loaded sections on a mission path model.
   * This highlights which sections (runs) are currently loaded in the viewer.
   * @param pathId The id of the trajectory/mission path
   * @param loadedSectionNames Array of section names that are currently loaded
   */
  public async updateLoadedSections(
    pathId: string,
    loadedSectionNames: string[]
  ): Promise<void> {
    this.desiredLoadedSections.set(pathId, loadedSectionNames);
    await this.applyMaterialsAndSections(pathId);
  }

  public async updateSectionMaterials(
    pathId: string,
    materials: SectionMaterialConfig[]
  ): Promise<void> {
    this.desiredSectionMaterials.set(pathId, materials);
    await this.applyMaterialsAndSections(pathId);
  }

  private applyMaterialsAndSections(pathId: string): Promise<void> {
    const previous = this.applyChains.get(pathId) ?? Promise.resolve();
    const next = previous
      .catch(() => undefined)
      .then(() => this.doApplyMaterialsAndSections(pathId));
    this.applyChains.set(pathId, next);
    return next;
  }

  private async doApplyMaterialsAndSections(pathId: string): Promise<void> {
    const model = this.viewer.getModel(getTrbModelName(pathId));
    if (!(model instanceof TrimbimModel)) {
      return;
    }
    const tool = this.viewer.tools.get(AlignmentNavigationTool);
    if (!tool) {
      return;
    }

    const stored = this.desiredSectionMaterials.get(pathId);
    const loadedSectionNames = this.desiredLoadedSections.get(pathId);
    if (!stored?.length && !loadedSectionNames) {
      return;
    }

    const painted = this.applyColorOverridesToMaterials(pathId, stored ?? []);
    const loaded = loadedSectionNames ?? [];
    const signature = `${sectionMaterialsSignature(painted)}|${JSON.stringify(
      [...loaded].sort()
    )}`;
    if (this.appliedMaterialsSignature.get(pathId) === signature) {
      return;
    }

    // Refresh the tool cache (also paints everything unloaded). Then paint
    // loaded sections from OUR painted copy — do not call setLoadedSections,
    // which reads a process-wide cache keyed by modelId and races across panes.
    if (painted.length > 0) {
      await tool.setSectionMaterials(model, painted);
      if (loadedSectionNames) {
        const loadedSet = new Set(loadedSectionNames);
        for (const { sectionName, material } of painted) {
          if (loadedSet.has(sectionName)) {
            await tool.setSectionMaterial(model, sectionName, material);
          }
        }
      }
    } else if (loadedSectionNames) {
      await tool.setLoadedSections(model, loadedSectionNames);
    }
    this.appliedMaterialsSignature.set(pathId, signature);
  }

  public removeTrajectory(id: string): void {
    const metadata = this.getById(id);
    if (metadata) {
      this.removeItem(metadata);
    }
    this.sourceBuffers.delete(id);
    this.colorOverrides.delete(id);
    this.desiredLoadedSections.delete(id);
    this.desiredSectionMaterials.delete(id);
    this.appliedMaterialsSignature.delete(id);
    this.applyChains.delete(id);
  }

  public async cleanup(): Promise<void> {
    this.setItems([]);
    this.sourceBuffers.clear();
    this.colorOverrides.clear();
    this.desiredLoadedSections.clear();
    this.desiredSectionMaterials.clear();
    this.appliedMaterialsSignature.clear();
    this.applyChains.clear();
  }
}

function sectionMaterialsSignature(materials: SectionMaterialConfig[]): string {
  return JSON.stringify(
    materials.map((m) => {
      const material = m.material as {
        color?: { getHexString?(): string };
        lineWidth?: number;
      };
      const color = material.color;
      const colorKey = color?.getHexString ? color.getHexString() : '';
      return [
        m.sectionName,
        colorKey,
        m.beginStation,
        m.endStation,
        material.lineWidth,
      ];
    })
  );
}

/**
 * Creates an independent deep copy of TrajectoryMetadata, cloning all
 * mutable arrays and collections so mutations on the clone never affect
 * the original (or vice-versa).
 */
function cloneTrajectoryMetadata(
  metadata: TrajectoryMetadata
): TrajectoryMetadata {
  return {
    ...metadata,
    sectionTags: [...metadata.sectionTags],
    sectionMaterials: metadata.sectionMaterials
      ? metadata.sectionMaterials.map((sm) => ({
          ...sm,
          material: { ...sm.material, color: sm.material.color?.clone() },
          defaultColor: sm.defaultColor?.clone(),
        }))
      : undefined,
    sectionChainages: metadata.sectionChainages
      ? metadata.sectionChainages.map((section) => ({
          ...section,
          markups: section.markups.map((m) => ({
            ...m,
            worldPosition: m.worldPosition?.clone(),
            perpendicularVector: m.perpendicularVector?.clone(),
          })),
          segmentWeb3dCumDists: new Float64Array(section.segmentWeb3dCumDists),
        }))
      : undefined,
    linesPerEntity: metadata.linesPerEntity
      ? new Map(metadata.linesPerEntity)
      : undefined,
    trimbimProperties: metadata.trimbimProperties
      ? {
          alignmentProperties: metadata.trimbimProperties.alignmentProperties
            ? [...metadata.trimbimProperties.alignmentProperties]
            : undefined,
        }
      : undefined,
  };
}
